
import IDimensions from '../model/IDimensions';
import ILocale from '../model/ILocale';
import { IPriceOverride } from '../model/IPrice';
import { IPriceDisplay } from '../model/IPriceDisplay';
import priceFormatterFactory from './priceFormatterFactory';

function buildId(dimension: IDimensions): string {
  return `${dimension.country}_${dimension.brand}_priceDisplay`;
}

export const dataLayerPriceDisplay = {
  get: (dimension: IDimensions) => {
    return localStorage.getItem(buildId(dimension)) ?? 'null';
  },
  set: (dimension: IDimensions, value: IPriceDisplay) => {
    localStorage.setItem(buildId(dimension), JSON.stringify(value));
  },
};

interface ExtractedPrices {
  primaryPrice: IPriceOverride;
  secondaryPrice: IPriceOverride;
}

type ExtendedPriceDisplay = IPriceDisplay & {
  displayPriceByType?: boolean;
  primary?: { priceType?: string; currency?: string };
  secondary?: { priceType?: string; currency?: string };
  primaryOptions?: { currency?: string; decimalScale?: number };
};

export const priceExtractionHandler = (
  prices: IPriceOverride[],
  dimension: any
): ExtractedPrices => {
  const priceDisplayRaw = dataLayerPriceDisplay.get(dimension);


  let priceDisplay: ExtendedPriceDisplay | null;

  try {
    priceDisplay = JSON.parse(priceDisplayRaw) as ExtendedPriceDisplay;
  } catch (e) {
    priceDisplay = null;
  }

  const defaultPrice: IPriceOverride = {
    money: {
      amount: 0,
      currency: '',
    },
    type: '',
  };

  // Fallback in case priceDisplay is missing or corrupted
  if (!priceDisplay) {
    return {
      primaryPrice: defaultPrice,
      secondaryPrice: defaultPrice,
    };
  }

  const isDisplayPriceByType = priceDisplay.displayPriceByType;

  const isPrimaryPrice = isDisplayPriceByType
    ? (price: IPriceOverride) => price.type === priceDisplay.primary.priceType
    : (price: IPriceOverride) => price.money.currency === priceDisplay.primary.currency;

  const isSecondaryPrice = isDisplayPriceByType
    ? (price: IPriceOverride) => price.type === priceDisplay?.secondary?.priceType
    : (price: IPriceOverride) => price.money.currency === priceDisplay?.secondary?.currency;

  const getPrimaryPrice = prices.find(isPrimaryPrice) ?? defaultPrice;
  const getSecondaryPrice = prices.find(isSecondaryPrice) ?? defaultPrice;

  return {
    primaryPrice: getPrimaryPrice,
    secondaryPrice: getSecondaryPrice,
  };
};

export const currencyToNumber = (dimension: IDimensions, locale: ILocale, value: number) => {
  if (!value) { return 0 }
  const priceDisplay = JSON.parse(dataLayerPriceDisplay.get(dimension)) as ExtendedPriceDisplay;
  const priceArgs = {
    currency: priceDisplay?.primaryOptions?.currency,
    minimumFractionDigits: priceDisplay?.primaryOptions?.decimalScale,
    maximumFractionDigits: priceDisplay?.primaryOptions?.decimalScale,
    style: 'decimal',
    currencyDisplay: 'symbol',
  }
  const currencyLocale: string = locale.getCurrencyLocaleCode(dimension.language, dimension.country, priceDisplay?.primaryOptions?.currency);
  return parseFloat(priceFormatterFactory.create(priceArgs, currencyLocale).format((value / Math.pow(10, priceDisplay?.primaryOptions?.decimalScale))).replace(/[^0-9.]/g, ''))

}